// ProcessModeler.js — Moteur de modélisation visuelle de processus (style BPMN à couloirs).
// Auto-layout par niveaux (longest-path, détection des boucles) + couloirs par acteur + nœuds typés
// (début / tâche / décision / fin) + transitions animées (succès / échec / boucle) + sélection qui
// éclaire le chemin + panneau de détail + zoom/pan. 100% SVG/HTML, aucune dépendance de graphe externe.
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZoomIn, ZoomOut, Maximize2, X, ArrowRight, Clock, User, GitBranch } from 'lucide-react';
import { Icon, Badge } from '../ui';

const COL_W = 240, SLOT_H = 124, LANE_PAD = 18, LABEL_W = 128, MX = 28, MY = 16;
const DIM = { start: { w: 156, h: 48 }, end: { w: 156, h: 48 }, decision: { w: 104, h: 104 }, task: { w: 184, h: 70 } };
const KIND = {
  manual: { color: '#3B82F6', label: 'Manuel' },
  automatic: { color: '#64748B', label: 'Automatique' },
  ai: { color: '#8B5CF6', label: 'IA' },
};
const EDGE = {
  default: { color: '#94A3B8', label: 'Séquence' },
  success: { color: '#22C55E', label: 'Succès' },
  failure: { color: '#EF4444', label: 'Échec' },
  loop: { color: '#F59E0B', label: 'Boucle' },
};
const dimOf = (t) => DIM[t] || DIM.task;

// ---- Auto-layout ------------------------------------------------------------
function buildLayout(data) {
  const nodes = data.nodes || [];
  const edges = data.edges || [];
  const lanes = data.lanes || [{ id: '_', label: '', color: '#3B82F6' }];
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n; });

  // Détection des arêtes retour (boucles) via DFS.
  const adj = {}; nodes.forEach((n) => { adj[n.id] = []; });
  edges.forEach((e) => { if (byId[e.from] && byId[e.to]) adj[e.from].push(e.to); });
  const back = new Set(); const stateMap = {}; const stack = new Set();
  const roots = nodes.filter((n) => n.type === 'start').map((n) => n.id);
  const order = roots.length ? roots : nodes.map((n) => n.id);
  const dfs = (u) => {
    stateMap[u] = 'gray'; stack.add(u);
    for (const v of adj[u]) {
      if (stack.has(v)) back.add(`${u}->${v}`);
      else if (stateMap[v] !== 'black') dfs(v);
    }
    stack.delete(u); stateMap[u] = 'black';
  };
  order.forEach((r) => { if (stateMap[r] !== 'black') dfs(r); });
  nodes.forEach((n) => { if (stateMap[n.id] !== 'black') dfs(n.id); });

  // Niveaux : longest-path sur le DAG forward (Kahn).
  const fwd = edges.filter((e) => !back.has(`${e.from}->${e.to}`) && byId[e.from] && byId[e.to]);
  const level = {}; nodes.forEach((n) => { level[n.id] = 0; });
  const fadj = {}; const indeg = {}; nodes.forEach((n) => { fadj[n.id] = []; indeg[n.id] = 0; });
  fwd.forEach((e) => { fadj[e.from].push(e.to); indeg[e.to] += 1; });
  const q = nodes.filter((n) => indeg[n.id] === 0).map((n) => n.id);
  const ind = { ...indeg };
  while (q.length) {
    const u = q.shift();
    for (const v of fadj[u]) { level[v] = Math.max(level[v], level[u] + 1); if (--ind[v] === 0) q.push(v); }
  }
  const maxLevel = Math.max(0, ...nodes.map((n) => level[n.id]));

  // Couloirs : nb de slots = max de nœuds d'un même couloir à un même niveau.
  const laneIndex = {}; lanes.forEach((l, i) => { laneIndex[l.id] = i; });
  const cell = {}; // laneId -> level -> [nodeIds]
  lanes.forEach((l) => { cell[l.id] = {}; });
  nodes.forEach((n) => {
    const lid = byId[n.id].lane in cell ? n.lane : lanes[0].id;
    (cell[lid][level[n.id]] = cell[lid][level[n.id]] || []).push(n.id);
  });
  const slots = {}; lanes.forEach((l) => { slots[l.id] = Math.max(1, ...Object.values(cell[l.id]).map((a) => a.length), 1); });

  // Position des couloirs (bandes empilées).
  let y = MY; const laneBox = {};
  lanes.forEach((l) => {
    const h = slots[l.id] * SLOT_H + LANE_PAD * 2;
    laneBox[l.id] = { ...l, top: y, height: h };
    y += h;
  });
  const height = y + MY;
  const width = MX * 2 + LABEL_W + (maxLevel + 1) * COL_W;

  // Position des nœuds.
  const placed = nodes.map((n) => {
    const lid = n.lane in cell ? n.lane : lanes[0].id;
    const lvl = level[n.id];
    const group = cell[lid][lvl];
    const sub = group.indexOf(n.id);
    const box = laneBox[lid];
    const usable = box.height - LANE_PAD * 2;
    const cellH = usable / slots[lid];
    const cx = MX + LABEL_W + lvl * COL_W + COL_W / 2;
    const cy = box.top + LANE_PAD + sub * cellH + cellH / 2;
    const d = dimOf(n.type);
    return { ...n, level: lvl, cx, cy, w: d.w, h: d.h };
  });
  const pById = {}; placed.forEach((n) => { pById[n.id] = n; });

  // Couleur d'un nœud "fin" selon l'arête entrante (succès/échec).
  placed.forEach((n) => {
    if (n.type === 'end') {
      const inc = edges.find((e) => e.to === n.id);
      n.endColor = inc && inc.type === 'failure' ? '#EF4444' : inc && inc.type === 'success' ? '#22C55E' : '#64748B';
    }
  });

  // Chemins des arêtes.
  const links = edges.map((e, i) => {
    const s = pById[e.from]; const t = pById[e.to];
    if (!s || !t) return null;
    const isBack = back.has(`${e.from}->${e.to}`) || t.level < s.level;
    let d, lx, ly;
    if (isBack) {
      const sx = s.cx, sy = s.cy + s.h / 2, tx = t.cx, ty = t.cy + t.h / 2, dip = 64;
      d = `M ${sx} ${sy} C ${sx} ${sy + dip}, ${tx} ${ty + dip}, ${tx} ${ty}`;
      lx = (sx + tx) / 2; ly = Math.max(sy, ty) + dip - 6;
    } else {
      const sx = s.cx + s.w / 2, sy = s.cy, tx = t.cx - t.w / 2, ty = t.cy;
      const dx = Math.max(34, (tx - sx) * 0.5);
      d = `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`;
      lx = (sx + tx) / 2; ly = (sy + ty) / 2 - 8;
    }
    return { ...e, id: `${e.from}->${e.to}-${i}`, d, lx, ly, isBack, color: (EDGE[e.type] || EDGE.default).color };
  }).filter(Boolean);

  return { nodes: placed, links, lanes: Object.values(laneBox), width, height, maxLevel };
}

// ---- Rendu d'un nœud --------------------------------------------------------
const NodeCard = ({ n, dimmed, selected, onClick }) => {
  const common = { left: n.cx - n.w / 2, top: n.cy - n.h / 2, width: n.w, height: n.h };
  const ring = selected ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-transparent' : '';
  const op = dimmed ? 'opacity-30' : 'opacity-100';

  if (n.type === 'decision') {
    return (
      <motion.button onClick={onClick} whileHover={{ scale: 1.05 }} className={`absolute ${op} transition-opacity`} style={common}>
        <span className={`absolute inset-0 rotate-45 rounded-lg bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-400 shadow ${ring}`} />
        <span className="absolute inset-0 flex flex-col items-center justify-center px-1 text-center">
          <GitBranch className="h-3.5 w-3.5 text-amber-500 mb-0.5" />
          <span className="text-[10px] leading-tight font-semibold text-gray-800 dark:text-gray-100">{n.name}</span>
        </span>
      </motion.button>
    );
  }
  if (n.type === 'start' || n.type === 'end') {
    const color = n.type === 'start' ? '#22C55E' : (n.endColor || '#64748B');
    return (
      <motion.button onClick={onClick} whileHover={{ scale: 1.05 }} className={`absolute ${op} transition-opacity flex items-center justify-center gap-2 rounded-full text-white text-sm font-semibold shadow ${ring}`} style={{ ...common, backgroundColor: color }}>
        <Icon name={n.icon || (n.type === 'start' ? 'Play' : 'Flag')} className="h-4 w-4" />
        {n.name}
      </motion.button>
    );
  }
  // task
  const k = KIND[n.kind] || KIND.automatic;
  return (
    <motion.button onClick={onClick} whileHover={{ scale: 1.03 }} className={`absolute ${op} transition-opacity text-left rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md ${ring}`} style={common}>
    <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: k.color }} />
    <span className="flex items-center gap-2 h-full pl-4 pr-2">
      <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${k.color}1A`, color: k.color }}><Icon name={n.icon || 'Square'} className="h-4 w-4" /></span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2">{n.name}</span>
        <span className="block text-[11px] text-gray-400 dark:text-gray-500 truncate">{n.role || k.label}{n.sla ? ` · ${n.sla}` : ''}</span>
      </span>
    </span>
    </motion.button>
  );
};

// ---- Composant principal ----------------------------------------------------
const ProcessModeler = ({ process }) => {
  const layout = useMemo(() => buildLayout(process), [process]);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => { setSelected(null); setZoom(1); setPan({ x: 0, y: 0 }); }, [process]);

  useEffect(() => {
    if (!panning) return undefined;
    const move = (e) => { const d = dragRef.current; if (d) setPan({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) }); };
    const up = () => setPanning(false);
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [panning]);

  // Voisinage du nœud sélectionné (pour éclairer le chemin).
  const neighbors = useMemo(() => {
    if (!selected) return null;
    const set = new Set([selected]);
    layout.links.forEach((l) => { if (l.from === selected) set.add(l.to); if (l.to === selected) set.add(l.from); });
    return set;
  }, [selected, layout]);

  const sel = selected ? layout.nodes.find((n) => n.id === selected) : null;
  const outgoing = selected ? layout.links.filter((l) => l.from === selected) : [];
  const incoming = selected ? layout.links.filter((l) => l.to === selected) : [];
  const nameById = (id) => layout.nodes.find((n) => n.id === id)?.name || id;

  const onWheel = (e) => { e.preventDefault(); setZoom((z) => Math.min(1.8, Math.max(0.45, z - e.deltaY * 0.0012))); };
  const startPan = (e) => { dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y }; setPanning(true); };
  const fit = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const edgeOpacity = (l) => (!selected ? 1 : (l.from === selected || l.to === selected ? 1 : 0.12));
  const edgeWidth = (l) => (selected && (l.from === selected || l.to === selected) ? 3.4 : 2);

  return (
    <div className="relative rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 overflow-hidden" style={{ height: 600 }}>
      {/* Contrôles */}
      <div className="absolute top-3 right-3 z-20 flex gap-1 bg-white dark:bg-gray-800 rounded-lg shadow p-1">
        <button onClick={() => setZoom((z) => Math.min(1.8, z + 0.1))} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" title="Zoom avant"><ZoomIn size={16} /></button>
        <button onClick={() => setZoom((z) => Math.max(0.45, z - 0.1))} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" title="Zoom arrière"><ZoomOut size={16} /></button>
        <button onClick={fit} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" title="Réinitialiser"><Maximize2 size={16} /></button>
        <span className="px-2 flex items-center text-xs text-gray-500 dark:text-gray-400">{Math.round(zoom * 100)}%</span>
      </div>

      {/* Légende */}
      <div className="absolute bottom-3 left-3 z-20 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-lg shadow px-3 py-2 text-[11px] text-gray-600 dark:text-gray-300 flex flex-wrap gap-x-3 gap-y-1 max-w-[60%]">
        <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-full" style={{ background: '#22C55E' }} />Succès</span>
        <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-full" style={{ background: '#EF4444' }} />Échec</span>
        <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-full" style={{ background: '#F59E0B' }} />Boucle</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm rotate-45 border border-amber-400" />Décision</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: '#3B82F6' }} />Manuel</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: '#8B5CF6' }} />IA</span>
      </div>

      {/* Viewport zoom/pan */}
      <div className={`absolute inset-0 ${panning ? 'cursor-grabbing' : 'cursor-grab'}`} onMouseDown={startPan} onWheel={onWheel} onClick={() => setSelected(null)}>
        <div className="absolute origin-top-left" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, width: layout.width, height: layout.height }} onClick={(e) => e.stopPropagation()}>
          {/* Bandes de couloirs */}
          {layout.lanes.map((l, i) => (
            <div key={l.id} className={`absolute left-0 ${i % 2 ? 'bg-gray-100/50 dark:bg-white/5' : 'bg-transparent'}`} style={{ top: l.top, height: l.height, width: layout.width }}>
              <div className="absolute left-0 top-0 bottom-0 flex items-center" style={{ width: LABEL_W }}>
                <span className="ml-2 h-[70%] w-1.5 rounded-full" style={{ backgroundColor: l.color }} />
                <span className="ml-2 text-xs font-semibold uppercase tracking-wide" style={{ color: l.color }}>{l.label}</span>
              </div>
            </div>
          ))}

          {/* Arêtes (SVG) */}
          <svg className="absolute top-0 left-0" width={layout.width} height={layout.height} style={{ pointerEvents: 'none', overflow: 'visible' }}>
            <defs>
              {Object.entries(EDGE).map(([k, v]) => (
                <marker key={k} id={`arr-${k}`} markerWidth="9" markerHeight="9" refX="7.5" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 z" fill={v.color} /></marker>
              ))}
            </defs>
            {layout.links.map((l) => (
              <g key={l.id} style={{ opacity: edgeOpacity(l), transition: 'opacity .2s' }}>
                <path d={l.d} fill="none" stroke={l.color} strokeWidth={edgeWidth(l)} strokeDasharray={l.isBack ? '6 5' : l.type === 'default' ? '0' : '0'} markerEnd={`url(#arr-${l.type || 'default'})`} />
                {!l.isBack && (
                  <circle r="3.2" fill={l.color} opacity={selected && l.from !== selected && l.to !== selected ? 0 : 0.85}>
                    <animateMotion dur="2.4s" repeatCount="indefinite" path={l.d} />
                  </circle>
                )}
              </g>
            ))}
          </svg>

          {/* Étiquettes d'arêtes */}
          {layout.links.filter((l) => l.label).map((l) => (
            <div key={`lbl-${l.id}`} className="absolute -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm border"
              style={{ left: l.lx, top: l.ly, pointerEvents: 'none', opacity: edgeOpacity(l), color: l.color, borderColor: `${l.color}66`, backgroundColor: 'rgba(255,255,255,0.92)' }}>
              {l.label}
            </div>
          ))}

          {/* Nœuds */}
          {layout.nodes.map((n) => (
            <NodeCard key={n.id} n={n} selected={selected === n.id} dimmed={!!neighbors && !neighbors.has(n.id)} onClick={() => setSelected(n.id)} />
          ))}
        </div>
      </div>

      {/* Panneau de détail */}
      <AnimatePresence>
        {sel && (
          <motion.div initial={{ x: 360, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 360, opacity: 0 }} transition={{ type: 'tween', duration: 0.22 }}
            className="absolute top-0 right-0 bottom-0 z-30 w-[340px] max-w-[88%] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl overflow-y-auto">
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${(KIND[sel.kind] || KIND.automatic).color}1A`, color: (KIND[sel.kind] || KIND.automatic).color }}><Icon name={sel.icon || 'Square'} className="h-5 w-5" /></span>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white leading-tight">{sel.name}</h4>
                    <span className="text-xs text-gray-400">{sel.type === 'task' ? 'Tâche' : sel.type === 'decision' ? 'Décision' : sel.type === 'start' ? 'Début' : 'Fin'}</span>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
              </div>

              {sel.description && <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{sel.description}</p>}

              <div className="mt-3 flex flex-wrap gap-2">
                {sel.kind && <Badge color={(KIND[sel.kind] || {}).color}>{(KIND[sel.kind] || {}).label}</Badge>}
                {sel.role && <Badge><User className="h-3 w-3" />{sel.role}</Badge>}
                {sel.sla && <Badge color="#F59E0B"><Clock className="h-3 w-3" />{sel.sla}</Badge>}
              </div>

              {incoming.length > 0 && (
                <div className="mt-5">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Entrant</h5>
                  <div className="space-y-1.5">
                    {incoming.map((l) => (
                      <button key={l.id} onClick={() => setSelected(l.from)} className="w-full flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                        <span className="truncate">{nameById(l.from)}</span><ArrowRight className="h-3 w-3 shrink-0" style={{ color: l.color }} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {outgoing.length > 0 && (
                <div className="mt-5">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Transitions sortantes</h5>
                  <div className="space-y-2">
                    {outgoing.map((l) => (
                      <button key={l.id} onClick={() => setSelected(l.to)} className="w-full text-left p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-100 flex items-center gap-1"><ArrowRight className="h-3.5 w-3.5" style={{ color: l.color }} />{nameById(l.to)}</span>
                          {l.label && <Badge color={l.color}>{l.label}</Badge>}
                        </div>
                        {l.condition && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Condition : {l.condition}</p>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {sel.type === 'end' && outgoing.length === 0 && <p className="mt-5 text-sm text-gray-400">État final du processus.</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProcessModeler;
